import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar, Users, Heart, Code, CheckCircle, Video, Lightbulb, GraduationCap } from "lucide-react";
import EventbriteRegistrationFlow, { RegistrationFlowResult } from "@/components/EventbriteRegistrationFlow";
import LivestreamRegistrationForm from "@/components/LivestreamRegistrationForm";
import ChallengeJoinFlow from "@/components/challenges/ChallengeJoinFlow";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const Attendees: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLivestreamThankYou, setShowLivestreamThankYou] = useState(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationFlowResult | null>(null);
  const [formMode, setFormMode] = useState<"in-person" | "livestream">("in-person");
  
  // Team selection state
  const [showTeamSelection, setShowTeamSelection] = useState(false);
  const [challengeForTeam, setChallengeForTeam] = useState<{
    id: string;
    title: string;
    event_id: string | null;
  } | null>(null);

  useEffect(() => {
    if (location.hash === '#register-form') {
      setTimeout(() => {
        document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleLivestreamSuccess = () => {
    setShowLivestreamThankYou(true);
    document.getElementById('thank-you')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToForm = () => {
    document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRegistrationComplete = async (result: RegistrationFlowResult) => {
    setRegistrationResult(result);
    
    if (result.isTeamParticipant) {
      // Fetch the featured challenge for team selection
      const { data: challenge } = await supabase
        .from("challenges")
        .select("id, title, event_id")
        .eq("is_featured", true)
        .limit(1)
        .maybeSingle();
      
      if (challenge) {
        setChallengeForTeam(challenge);
        setShowTeamSelection(true);
        toast.success("Registration complete! Now let's find you a team.");
      } else {
        // No featured challenge - redirect to challenges page as fallback
        toast.success("Registration complete! Browse challenges to join a team.");
        navigate("/challenges", {
          state: {
            openJoinFlow: true,
            registrationId: result.registrationId,
          }
        });
      }
    } else {
      // Non-participant - show success message
      setShowRegistrationSuccess(true);
      document.getElementById('thank-you')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleTeamFlowClose = (open: boolean) => {
    setShowTeamSelection(open);
    if (!open) {
      // User completed or closed team flow - show success
      setShowRegistrationSuccess(true);
      document.getElementById('thank-you')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Attend BuildWithChrist.ai - Learn AI by Making a Difference</title>
        <meta name="description" content="Join a 4-hour build event where you'll learn AI by creating solutions that help nonprofits. No experience required. Family-friendly with childcare available." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px]" />
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight font-display">
            <span className="text-gradient">Learn AI by Making a Difference</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join a focused 4-hour build event where you'll gain hands-on AI experience while helping nonprofits accelerate their impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" className="text-lg" onClick={scrollToForm}>
              Register to Attend
            </Button>
          </div>
        </div>
      </section>

      {/* What You'll Experience */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-display">What You'll Experience</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-card border border-border p-6 rounded-xl">
              <GraduationCap className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Learn by Doing</h3>
              <p className="text-sm text-muted-foreground">Hands-on training tables and mentorship for all skill levels, from beginners to experienced builders.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Heart className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Meaningful Work</h3>
              <p className="text-sm text-muted-foreground">Build real AI tools that help real nonprofits solve real problems in their communities.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Users className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Community Connection</h3>
              <p className="text-sm text-muted-foreground">Network with mission-driven peers, potential employers, and nonprofit leaders.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Lightbulb className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Lightning Talks</h3>
              <p className="text-sm text-muted-foreground">Short, high-impact talks from experts demystifying AI with practical workflows.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Code className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Watch Teams Build</h3>
              <p className="text-sm text-muted-foreground">See how AI solutions are created live, from problem definition to working prototype.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Calendar className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Family-Friendly</h3>
              <p className="text-sm text-muted-foreground">Supervised childcare and optional kids' AI activities available during the event.</p>
            </div>
          </div>
        </div>
      </section>

      {/* No Experience Required */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold font-display">No Experience Required</h2>
          <p className="text-lg text-muted-foreground">
            Whether you're curious about AI or already building with it, there's a place for you. Our training tables, mentors, and starter workflows ensure everyone can meaningfully participate and learn.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            <div className="bg-card border border-border p-4 rounded-lg text-left">
              <p className="font-semibold text-foreground mb-2">For Beginners</p>
              <p className="text-sm text-muted-foreground">Join training tables focused on AI basics, content creation, and automation. Learn from mentors who guide you step by step.</p>
            </div>
            <div className="bg-card border border-border p-4 rounded-lg text-left">
              <p className="font-semibold text-foreground mb-2">For Experienced Builders</p>
              <p className="text-sm text-muted-foreground">Join or lead a team working on a real nonprofit challenge. Apply your skills to problems that matter.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register-form" className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          {showLivestreamThankYou ? (
            <div id="thank-you" className="text-center space-y-6 py-12 max-w-xl mx-auto">
              <div className="mx-auto w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-secondary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-display">
                Livestream Registration Received!
              </h2>
              <p className="text-lg text-muted-foreground">
                We'll send you the livestream link and details before the event. See you there!
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowLivestreamThankYou(false);
                  setFormMode("in-person");
                }}
                size="sm"
              >
                Register Another Person
              </Button>
            </div>
          ) : showRegistrationSuccess && !registrationResult?.isTeamParticipant ? (
            <div id="thank-you" className="text-center space-y-6 py-12 max-w-xl mx-auto">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-display">
                Registration Complete!
              </h2>
              <p className="text-lg text-muted-foreground">
                You're all set for the event. We'll send you details as the date approaches.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/dashboard")}>
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowRegistrationSuccess(false);
                    setRegistrationResult(null);
                  }}
                >
                  Register Another Person
                </Button>
              </div>
            </div>
          ) : showRegistrationSuccess && registrationResult?.isTeamParticipant ? (
            <div id="thank-you" className="text-center space-y-6 py-12 max-w-xl mx-auto">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-display">
                You're All Set!
              </h2>
              <p className="text-lg text-muted-foreground">
                Registration and team selection complete. We'll send you details as the date approaches.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/dashboard")}>
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/challenges")}
                >
                  Browse Challenges
                </Button>
              </div>
            </div>
          ) : (
          <div className="max-w-xl mx-auto space-y-6">
            {/* Only show title for livestream mode - in-person has title inside EventbriteRegistrationFlow */}
            {formMode === "livestream" && (
              <div className="text-center space-y-2">
                <h2 className="text-2xl md:text-3xl font-bold font-display">
                  Join the Livestream
                </h2>
                <p className="text-muted-foreground">
                  Watch teams build, learn from lightning talks, and be part of the community from anywhere.
                </p>
              </div>
            )}
            
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              {formMode === "in-person" ? (
                <EventbriteRegistrationFlow
                  onComplete={handleRegistrationComplete}
                  showEventbriteFirst={false}
                />
              ) : (
                <LivestreamRegistrationForm onSuccess={handleLivestreamSuccess} />
              )}
            </div>
              
              <div className="p-4 rounded-lg bg-muted/50 border border-border text-center">
                {formMode === "in-person" ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Can't attend in person?</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFormMode("livestream")}
                      className="gap-2"
                    >
                      <Video className="w-4 h-4" />
                      Join Virtually
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Want to attend in person?</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFormMode("in-person")}
                      className="gap-2"
                    >
                      <Users className="w-4 h-4" />
                      Register In Person
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Team Selection Dialog */}
      {challengeForTeam && (
        <ChallengeJoinFlow
          open={showTeamSelection}
          onOpenChange={handleTeamFlowClose}
          challengeId={challengeForTeam.id}
          eventId={challengeForTeam.event_id}
          challengeTitle={challengeForTeam.title}
          initialAction="join"
          startAtStep={2}
          preRegistrationId={registrationResult?.registrationId}
        />
      )}
    </div>
  );
};

export default Attendees;
